import React, { useState, useEffect, useRef, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const SUGGESTIONS = [
  "Analisis pola pengeluaranku bulan ini dan berikan saran hemat konkret.",
  "Bagaimana progres tujuan tabungan saya? Berapa lama lagi mencapainya?",
  "Kategori mana yang paling boros dan strategi menguranginya?",
  "Berdasarkan cash flow saya, berapa yang aman untuk diinvestasikan bulan ini?",
];

const renderTextWithBoldAndHighlights = (text, plainBold = false) => {
  if (!text) return "";
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      // Give a cool background highlight if it contains numbers/money or durations
      if (!plainBold && (boldText.match(/\d+/) || boldText.toLowerCase().includes("bulan") || boldText.toLowerCase().includes("tahun"))) {
        return (
          <strong key={i} className="bg-ai/10 text-ai px-1.5 py-0.5 rounded font-semibold font-mono text-xs mx-0.5 inline-block">
            {boldText}
          </strong>
        );
      }
      return (
        <strong key={i} className="font-semibold text-foreground">
          {boldText}
        </strong>
      );
    }
    return part;
  });
};

const renderInsightContent = (text) => {
  if (!text) return null;
  // Clean LaTeX math delimiters globally
  const cleanText = text.replace(/\\\(|\\\)/g, "");

  const lines = cleanText.split("\n");
  return lines.map((line, idx) => {
    let trimmed = line.trim();
    if (!trimmed) return <div key={idx} className="h-2" />;

    // 0. Headings: "### Heading"
    if (trimmed.startsWith("### ")) {
      const title = trimmed.replace("### ", "").trim();
      return (
        <h4 key={idx} className="font-heading text-xs font-semibold text-ai mt-4 mb-2 uppercase tracking-wider flex items-center gap-1.5">
          <span>{title}</span>
        </h4>
      );
    }

    // 0.5. Check if the line looks like a math calculation (e.g. "1.500.000 - 55.000 = Rp 1.445.000")
    if (trimmed.includes("=") && trimmed.match(/\d+/)) {
      // Clean up bullet point prefixes if any
      let cleanLine = trimmed;
      if (cleanLine.startsWith("- ")) cleanLine = cleanLine.substring(2);
      else if (cleanLine.startsWith("* ")) cleanLine = cleanLine.substring(2);

      return (
        <div key={idx} className="my-2 pl-4">
          <span className="font-mono text-xs bg-secondary/60 text-foreground border border-border px-3 py-1.5 rounded inline-block shadow-sm leading-relaxed">
            {renderTextWithBoldAndHighlights(cleanLine, true)}
          </span>
        </div>
      );
    }

    // 1. Heading detect: "1. **Beli Sepatu**" or "### Heading" or "**Beli Sepatu**"
    const headingMatch = trimmed.match(/^(\d+\.\s+)?\*\*(.+?)\*\*/);
    if (headingMatch) {
      const num = headingMatch[1] || "";
      const title = headingMatch[2];
      return (
        <h3 key={idx} className="font-heading text-base font-semibold text-foreground mt-4 mb-2 flex items-center gap-1.5">
          {num && <span className="text-ai/85 font-mono">{num}</span>}
          <span>{title}</span>
        </h3>
      );
    }

    // 2. Key-value detect: "- Target: Rp 1.500.000" or "- Terkumpul: Rp 100.000" or "- Sisa: Rp 1.400.000"
    const kvMatch = trimmed.match(/^[-*]\s+\*\*(.+?)\*\*:\s*(.+)$/) || trimmed.match(/^[-*]\s+(.+?):\s*(.+)$/);
    if (kvMatch) {
      const key = kvMatch[1].replace(/\*\*/g, "").trim();
      const val = kvMatch[2].trim();

      // Determine badge color based on key
      let badgeStyle = "bg-secondary text-secondary-foreground border border-border";
      let keyText = key;
      if (key.toLowerCase().includes("target")) {
        badgeStyle = "bg-blue-500/10 text-blue-500 border border-blue-500/20";
      } else if (key.toLowerCase().includes("terkumpul") || key.toLowerCase().includes("tabungan")) {
        badgeStyle = "bg-gain/10 text-gain border border-gain/20";
      } else if (key.toLowerCase().includes("sisa")) {
        badgeStyle = "bg-loss/10 text-loss border border-loss/20 font-medium";
      }

      const formattedVal = val.replace(/\*\*/g, "");

      return (
        <div key={idx} className="pl-4 py-1 flex items-baseline gap-2 text-sm">
          <span className="text-muted-foreground font-medium min-w-[100px] inline-block">{keyText}:</span>
          <span className={`px-2 py-0.5 rounded text-xs font-mono font-medium ${badgeStyle}`}>
            {formattedVal}
          </span>
        </div>
      );
    }

    // 3. Regular bullet point: "- [text]"
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const content = trimmed.substring(2);
      return (
        <ul key={idx} className="list-disc pl-6 text-sm text-foreground/85 my-1">
          <li>{renderTextWithBoldAndHighlights(content)}</li>
        </ul>
      );
    }

    // 4. Default paragraph text
    return (
      <p key={idx} className="text-sm text-foreground/85 leading-relaxed my-1.5">
        {renderTextWithBoldAndHighlights(trimmed)}
      </p>
    );
  });
};

export default function AIInsight() {
  const { data: history } = useSWR("/ai/history", fetcher);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setLoading(true);
    try {
      await api.post("/ai/insight", { question: text });
      setQuestion("");
      mutate("/ai/history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI sedang sibuk, coba lagi");
    } finally { setLoading(false); }
  };

  // Reverse history so oldest is first, newest is last for chronological chat flow
  const chatMessages = useMemo(() => {
    return history ? [...history].reverse() : [];
  }, [history]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatMessages, loading]);

  return (
    <div className="animate-fade-up flex flex-col h-[calc(100vh-8rem)] min-h-[580px] max-h-[820px]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b border-border">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-ai" />
            <h1 className="font-heading text-xl font-medium tracking-tight">AI Advisor</h1>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Analisis transaksi, rekening, dan goals secara instan.
          </p>
        </div>
      </div>

      {/* Chat Container */}
      <div className="mt-4 flex-1 flex flex-col border border-border bg-card overflow-hidden rounded-none relative">
        {/* Messages Area */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-5 space-y-5 bg-secondary/5"
        >
          {chatMessages.length === 0 && !loading ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <Sparkles className="h-10 w-10 text-muted-foreground/20 mb-3 animate-pulse" />
              <p className="text-sm text-muted-foreground max-w-xs">
                Belum ada percakapan. Pilih saran di bawah atau ketik pertanyaan Anda.
              </p>
            </div>
          ) : (
            <>
              {chatMessages.map((msg) => (
                <div key={msg.id} className="space-y-4">
                  {/* User Bubble */}
                  <div className="flex justify-end">
                    <div className="bg-foreground text-background dark:bg-accent dark:text-foreground px-4 py-2.5 rounded-2xl rounded-tr-none max-w-[85%] sm:max-w-[70%] text-sm shadow-sm leading-relaxed">
                      <p className="font-semibold text-[10px] opacity-75 mb-0.5">Anda</p>
                      <p>{msg.question}</p>
                    </div>
                  </div>

                  {/* AI Bubble */}
                  <div className="flex justify-start">
                    <div className="bg-card border border-border px-4 py-4 rounded-2xl rounded-tl-none max-w-[90%] sm:max-w-[80%] text-sm shadow-sm flex items-start gap-3 w-full">
                      <div className="h-6 w-6 rounded-full bg-ai text-background flex items-center justify-center flex-shrink-0">
                        <Sparkles className="h-3 w-3" />
                      </div>
                      <div className="text-sm leading-relaxed text-foreground/90 space-y-1 w-full overflow-x-hidden">
                        <p className="font-heading text-xs text-ai font-semibold mb-2">AI Advisor</p>
                        {renderInsightContent(msg.answer)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border px-4 py-4 rounded-2xl rounded-tl-none max-w-[90%] sm:max-w-[80%] text-sm shadow-sm flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-ai text-background flex items-center justify-center flex-shrink-0 animate-spin">
                      <Loader2 className="h-3 w-3" />
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      Sedang menganalisis kondisi keuangan Anda...
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Suggestions Bar */}
        <div className="px-4 pt-3 pb-2 bg-card border-t border-border flex flex-wrap gap-1.5 max-h-[85px] overflow-y-auto">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              onClick={() => ask(s)}
              disabled={loading}
              className="text-[10px] sm:text-xs border border-border bg-background/50 hover:bg-accent text-muted-foreground hover:text-foreground px-2.5 py-1 rounded-full transition-colors disabled:opacity-50 truncate max-w-[200px] sm:max-w-[300px]"
              title={s}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Input Bar */}
        <div className="p-4 bg-card border-t border-border flex items-center gap-3">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && question.trim() && !loading) {
                e.preventDefault();
                ask();
              }
            }}
            placeholder="Tanyakan pola pengeluaran, saran hemat, progres tabungan..."
            className="flex-1 h-11"
            disabled={loading}
            data-testid="ai-question-input"
          />
          <Button
            onClick={() => ask()}
            disabled={loading || !question.trim()}
            className="bg-ai text-background hover:bg-ai/90 h-11 px-4"
            data-testid="ai-submit-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
